import { useEffect, useMemo, useState } from "react";

import { useAuth } from "@/modules/auth/hooks/useAuth";
import {
  type CorridorUnavailableReason,
  type PaymentCorridor,
  PaymentCorridorsService,
  type PaymentDirection,
} from "@/shared/services/payments/paymentCorridors.service";
import { type CorridorLimits, corridorLimitsOf } from "@/shared/utils/amountLimits";

// Routes and fees are changed by hand from the config screens, minutes apart at worst, and the backend
// already memoizes for 60 s. A session-scoped window keeps the payins page, the wallet cards and the
// dispersion wizard on a single request without ever pinning a stale verdict for long.
const STALE_AFTER_MS = 2 * 60_000;

interface CacheEntry {
  corridors: PaymentCorridor[];
  fetchedAt: number;
}

const entries = new Map<string, CacheEntry>();
const inflight = new Map<string, Promise<PaymentCorridor[]>>();

/** Drops the memo — call after staff edit a route or a fee so the gating reflects it without a reload. */
export function invalidateCorridors(): void {
  entries.clear();
  inflight.clear();
}

async function ensureCorridors(key: string, organizationId?: string): Promise<PaymentCorridor[]> {
  const existing = entries.get(key);
  if (existing && Date.now() - existing.fetchedAt < STALE_AFTER_MS) return existing.corridors;

  let promise = inflight.get(key);
  if (!promise) {
    promise = PaymentCorridorsService.list(organizationId)
      .then((response) => {
        const corridors = response.corridors ?? [];
        entries.set(key, { corridors, fetchedAt: Date.now() });
        return corridors;
      })
      .finally(() => {
        inflight.delete(key);
      });
    inflight.set(key, promise);
  }
  return promise;
}

const corridorKey = (rail: string, country: string, currency: string, direction: PaymentDirection): string =>
  `${direction}:${country}:${currency}:${rail}`;

export interface UseAvailableCorridorsResult {
  corridors: PaymentCorridor[];
  isLoading: boolean;
  /** True until the first answer arrives, and forever if the endpoint is unreachable — see below. */
  isUnknown: boolean;
  /** Is this exact corridor runnable right now? */
  isAvailable: (rail: string, country: string, currency: string, direction: PaymentDirection) => boolean;
  /** Why this exact corridor cannot run — null while it can, or while the catalog is unknown. */
  reasonFor: (
    rail: string,
    country: string,
    currency: string,
    direction: PaymentDirection,
  ) => CorridorUnavailableReason | null;
  /** Any runnable corridor for this currency in this direction, whatever the rail. */
  hasCurrency: (currency: string, direction: PaymentDirection) => boolean;
  /** Rails that can run for a currency+direction — drives the method chips on a wallet card. */
  railsFor: (currency: string, direction: PaymentDirection) => string[];
  /**
   * Per-transaction bounds of this exact corridor, per payer kind — null while the catalog is unknown, when
   * the corridor is absent, or when it carries no bound at all. Null caps nothing client-side: the backend
   * rejects an out-of-range amount at preview/initiate (payins) and quote/create (payouts) regardless.
   */
  limitsFor: (
    rail: string,
    country: string,
    currency: string,
    direction: PaymentDirection,
  ) => CorridorLimits | null;
}

/**
 * Which corridors the current organization can actually operate on.
 *
 * **Fails open.** If the endpoint errors or has not answered yet, `isUnknown` is true and every predicate
 * returns true: a catalog that cannot be read must not black out a dashboard that worked yesterday. Callers
 * gate on `available === false`, never on "not present in a list I may not have". The same holds for the
 * amount limits: an unreadable catalog shows no hint and caps nothing — the backend is the guard.
 *
 * An INTERNAL reader is looking at another organization's data, so it must pass that org — commissions are
 * organization-scoped and so is the verdict.
 */
export function useAvailableCorridors(organizationId?: string): UseAvailableCorridorsResult {
  const { user, isInternalUser } = useAuth();
  // Members never send an org — the server pins them. Staff send the org they are LOOKING AT, falling back
  // to their own: on a surface with no org selector (the payins page, a wallet card) an internal user is
  // acting on their own organization, which is exactly the org the payin payload is built for. Without the
  // fallback the hook never fetched for staff at all, and every corridor silently failed open.
  const scopedOrgId = isInternalUser
    ? (organizationId ?? user?.organization_id ?? user?.organization?.id ?? undefined)
    : undefined;
  const key = user ? (scopedOrgId ?? "self") : null;

  const [corridors, setCorridors] = useState<PaymentCorridor[]>(() =>
    key ? (entries.get(key)?.corridors ?? []) : [],
  );
  const [isLoading, setIsLoading] = useState(false);
  const [resolved, setResolved] = useState(() => (key ? entries.has(key) : false));

  useEffect(() => {
    if (!key) {
      /* eslint-disable react-hooks-eslint/set-state-in-effect */
      setCorridors([]);
      setResolved(false);
      setIsLoading(false);
      /* eslint-enable react-hooks-eslint/set-state-in-effect */
      return;
    }

    let cancelled = false;
    setIsLoading(true);
    ensureCorridors(key, scopedOrgId)
      .then((next) => {
        if (cancelled) return;
        setCorridors(next);
        setResolved(true);
      })
      .catch(() => {
        // Fail open: an unreadable catalog leaves everything enabled rather than hiding corridors that
        // may well be working. `resolved` stays false so callers can tell "unknown" from "empty".
        if (!cancelled) setCorridors([]);
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [key, scopedOrgId]);

  const index = useMemo(() => {
    const byCorridor = new Map<string, PaymentCorridor>();
    for (const corridor of corridors) {
      byCorridor.set(
        corridorKey(corridor.rail, corridor.country, corridor.currency, corridor.direction),
        corridor,
      );
    }
    return byCorridor;
  }, [corridors]);

  return useMemo(() => {
    const isUnknown = !resolved;
    const runnable = (corridor: PaymentCorridor | undefined): boolean => corridor?.available === true;

    return {
      corridors,
      isLoading,
      isUnknown,
      isAvailable: (rail, country, currency, direction) =>
        isUnknown || runnable(index.get(corridorKey(rail, country, currency, direction))),
      reasonFor: (rail, country, currency, direction) =>
        isUnknown
          ? null
          : (index.get(corridorKey(rail, country, currency, direction))?.unavailable_reason ?? null),
      hasCurrency: (currency, direction) =>
        isUnknown ||
        corridors.some(
          (c) =>
            c.available && c.direction === direction && c.currency.toUpperCase() === currency.toUpperCase(),
        ),
      railsFor: (currency, direction) =>
        corridors
          .filter(
            (c) =>
              c.available && c.direction === direction && c.currency.toUpperCase() === currency.toUpperCase(),
          )
          .map((c) => c.rail),
      limitsFor: (rail, country, currency, direction) =>
        isUnknown
          ? null
          : corridorLimitsOf(index.get(corridorKey(rail, country, currency, direction))?.limits),
    };
  }, [corridors, index, isLoading, resolved]);
}
